import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	inject,
	viewChild,
	signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ComponentsModule } from '@components/components.module';
import { FormInstanceViewerComponent } from '@components/matter-form-instance-viewer/matter-form-instance-viewer.component';
import { Logger } from '@core/logger';
import { ICON } from '@core/util/icon';
import { FormInstance } from '@models/global-forms';
import { GlobalFormsVersionsRepo } from '@repos/global-forms-versions.ts/global-forms-verisons.repo';
import { GlobalFormsRepo } from '@repos/global-forms/global-forms.repo';
import { GlobalFormsPdfService } from '@services/pdf/global-forms-pdf.service';
import { catchError, finalize, map, Observable, of, switchMap } from 'rxjs';
import { AtomicModule } from 'src/app/shared/atomic/atomic.module';

@Component({
	selector: 'app-generic-form-pdf-viewer',
	imports: [ComponentsModule, CommonModule, AtomicModule],
	templateUrl: './generic-form-pdf-viewer.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericFormPdfViewerComponent {
	pdfViewerInstanceViewer = viewChild('pdfViewerInstanceViewer', {
		read: FormInstanceViewerComponent,
	});
	ICON = ICON;
	globalFormsRepo = inject(GlobalFormsRepo);
	globalFormsVersionRepo = inject(GlobalFormsVersionsRepo);
	route = inject(ActivatedRoute);
	globalFormsPdfService = inject(GlobalFormsPdfService);
	loading_ = signal<boolean>(true);
	error_ = signal<boolean>(false);

	formName$: Observable<string> = this.route.params.pipe(
		map((params) => params['formName']),
	);
	form$: Observable<FormInstance> = this.formName$.pipe(
		switchMap((id) => this.globalFormsRepo.Get$(id)),
		switchMap((form) => {
			if (!form?.currentVersion) {
				Logger.error(
					`Form id:${form?.id} - Form doesn't have a current version`,
				);
				this.loading_.set(false);
				this.error_.set(true);
				return of(null);
			}

			return this.globalFormsVersionRepo
				.Get$(form.currentVersion, { globalFormId: form.id })
				.pipe(
					switchMap(async (version) => ({
						...form,
						storageRefPath: version.storageRefPath,
						barcodeStorageRefPath: version.barcodeStorageRefPath,
						fields: await this.globalFormsPdfService.evaluateFields(
							Object.values(version.fieldMappings ?? {}),
							form?.context,
						),
						context: { id: form.id, name: form.name }, // TODO: check if this is correct
					})),
					finalize(() => {
						this.loading_.set(false);
					}),
				);
		}),
		catchError((error) => {
			Logger.error(`Error fetching form data: ${error}`);
			this.loading_.set(false);
			this.error_.set(true);
			return of(null);
		}),
	);

	download() {
		this.pdfViewerInstanceViewer()?.pdfViewer()?.download();
	}

	print() {
		this.pdfViewerInstanceViewer()?.pdfViewer()?.print();
	}
}
